/**
 * Tipos de vaga da oficina — a cabine de pintura como recurso (F3.5).
 *
 * Duas coisas diferentes moram na mesma tabela (`workshop_slots`), e a
 * diferença é COMO elas ficam ocupadas:
 *
 *   - `bay` — a vaga de sempre, de chão ou elevador. O carro para ali na
 *     abertura e fica a OS inteira: a ocupação é o `service_orders.slot_id`.
 *   - `paint_booth` — a cabine de pintura. O carro passa por ela só numa etapa
 *     do processo, então a ocupação é a ETAPA da OS ("Cabine de pintura"), e a
 *     OS nunca aponta para a cabine pelo `slot_id`.
 *
 * Por isso a cabine sai de tudo que fala de "vaga": da escolha na abertura da
 * OS, do mapa de ocupação e da contagem de vagas livres. Uma cabine vazia não é
 * lugar para estacionar.
 */
import {
  PAINT_BOOTH_STAGE_KEY,
  resolveModuleStages,
  type ModuleStage,
  type WorkshopStageConfig,
} from './modules';

export const SLOT_KINDS = ['bay', 'paint_booth'] as const;

export type SlotKind = (typeof SLOT_KINDS)[number];

/** O tipo de toda vaga que já existia antes da F3.5 (migração 0104). */
export const DEFAULT_SLOT_KIND: SlotKind = 'bay';

export const SLOT_KIND_LABELS: Record<SlotKind, string> = {
  bay: 'Vaga',
  paint_booth: 'Cabine de pintura',
};

export function isSlotKind(value: unknown): value is SlotKind {
  return typeof value === 'string' && (SLOT_KINDS as readonly string[]).includes(value);
}

/**
 * A etapa que ocupa a cabine, e a que alimenta a fila dela.
 *
 * "Na cabine" é a OS de funilaria na etapa `pintura` — a chave, com o nome que
 * a oficina der ("Estufa"). Não há fila mantida à mão: decisão do dono do
 * produto (24/09), para a lista nunca ficar desatualizada.
 */
export const PAINT_BOOTH_STAGE: ModuleStage = PAINT_BOOTH_STAGE_KEY;

/**
 * A cabine está no processo desta oficina? Escondida, a oficina pinta fora
 * (F3.8): o card da cabine some, e com ele a fila — "0 de 2 ocupadas" para
 * sempre seria um dado falso na tela.
 */
export function paintBoothEnabled(config?: WorkshopStageConfig): boolean {
  return resolveModuleStages('funilaria', config).some((s) => s.key === PAINT_BOOTH_STAGE && !s.hidden);
}

/**
 * "Próximos" da cabine: a etapa VISÍVEL imediatamente antes dela, na ordem da
 * oficina (F3.8, emenda o item 2 da F3.5). Com a ordem padrão é a
 * `preparacao`, como sempre foi; quem põe o "Lixamento" logo antes da estufa vê
 * na fila quem está lixando. Cabine escondida, ou primeira da lista, não tem
 * fila: `null`.
 */
export function paintBoothQueueStage(config?: WorkshopStageConfig): string | null {
  const visible = resolveModuleStages('funilaria', config).filter((s) => !s.hidden);
  const index = visible.findIndex((s) => s.key === PAINT_BOOTH_STAGE);
  return index > 0 ? visible[index - 1]!.key : null;
}
